import React, { useEffect } from "react";
import { getForm, postForm, uploadFiles, getMerchant, getMdaMerchant, getMdaForm } from "../apis/formApi";
import { DOMAIN, getFormName } from "../utils/constants";
import _uniqueId from 'lodash/uniqueId';
import { compareDate } from "../utils/functions/compareDate";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const useForm = () => {
  const [fetching, setFetching] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [merchant, setMerchant] = React.useState(null);
  const [error, setError] = React.useState(null);

  const [submitData, setSubmitData] = React.useState(null);
  const [submitError, setSubmitError] = React.useState(null);
  const [formExpired, setFormExpired] = React.useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const fetchMdaForm = async (tenantId, formName) => {
    setFetching(true);
    const [merchantResult, result] = await Promise.all([
      await getMdaMerchant(tenantId),
      await getMdaForm({ name: formName }),
    ]);
    setFetching(false);

    if (!result.ok) {
      return setError("The Form you requested does not exist");
    }
    if (!result.data.success) {
      return setError(result?.data?.data?.message || "");
    }

    if (
      result.data.success &&
      result.data.data &&
      result.data.data.length === 0
    ) {
      return setError("The Form you requested does not exist");
    }

    setMerchant(merchantResult?.data?.merchant || null)
    return setData(result.data);
  };

  const fetch = async () => {
    setFetching(true);
    const [merchantResult, result] = await Promise.all([
      await getMerchant(),
      await getForm({ name: getFormName() }),
    ]);
    setFetching(false);
    if (!result.ok) {
      return setError("The Form you requested does not exist");
    }
    if (!result.data.success) {
      return setError(result?.data?.data?.message || "");
    }

    if (
      result.data.success &&
      result.data.data &&
      result.data.data.length === 0
    ) {
      return setError("The Form you requested does not exist");
    }
    let data = result?.data?.data?.[0];
    if(data && data?.end_date) {
      setFormExpired(compareDate(data.end_date, "<"))
    }
    setMerchant(merchantResult?.data?.merchant || null);
    return setData(result.data);
  };

  const makeFormData = (data) => {
    const formData = new FormData();
    formData.append("ticket_id", data.ticket_id);
    formData.append("source", 'webform');
    data.files.forEach((file) => {
      formData.append("file[]", file.value, file.value.name);
    });

    return formData;
  };
 
  const submitForm = async ({ values, files = [], token, options, type, accessToken,selectedOption, merchant }) => {
    
    if (!executeRecaptcha) {
      return setSubmitError('reCAPTCHA not ready. Please refresh.'); 
    }
    let formName = getFormName();
    formName = formName.replace(/[^A-Za-z/_]/g, '_');
    
    const captchaToken = await executeRecaptcha(
      `${formName}_${DOMAIN}` // Tenant-specific action (e.g., "contact_yourtenant_123")
    );
    if (!captchaToken) {
      return setSubmitError('reCAPTCHA verification failed');
    }
    const params = new URLSearchParams(window.location.search);
    const { setSubmitting } = options;

    // const tenantId = params.get("tenantId");
    const auth_token = params.get("auth");

    const sso_token = params.get("accessToken");
   
    if (values.is_internal && !auth_token) {
      return setSubmitError(
        "Cannot validate form! Check the form and try again"
      );
    }
    if (values.is_internal && !sso_token) {
      return setSubmitError(
        "Cannot validate form! access token required for internal forms"
      );
    }

    const appToken = auth_token ? auth_token : token;
    const appAccessToken = sso_token ? sso_token : accessToken;
    const tenantId = merchant?.tenantId;

    // remove previously appended name suffix
    values.captchaToken = captchaToken;
    setSubmitting(true);
    const result = await postForm({ data: values, token: appToken, accessToken:appAccessToken, type, selectedOption, tenantId});

    if (!result.ok) {
      console.log(result.originalError);
      setSubmitting(false);
      return setSubmitError("Unable to submit form");
    }

    if (result.data && !result.data.success) {
      return setSubmitError(result.data.data.message);
    }

    if (files.length > 0) {
      const formData = makeFormData({
        ticket_id: result.data.data.ticket_id,
        files,
      });

      const uploadResult = await uploadFiles({ token, data: formData });
      setSubmitting(false);

      if (!uploadResult.ok) {
        console.log(uploadResult.originalError);
        return setSubmitError("Unable to upload files");
      }

      if (uploadResult.data && !uploadResult.data.success) {
        console.log(uploadResult.originalError);
        return setSubmitError(uploadResult.data.message);
      }
    }

    return setSubmitData(result.data);
  };


  useEffect(() => {
    if (getFormName().match(/webform/g) !== null) {
      setError("The Form you requested does not exist");
    }
  }, [getFormName()]);

  return {
    fetching,
    loading,
    fetch,
    fetchMdaForm,
    submitForm,
    error,
    submitData,
    submitError,
    data,
    merchant,
    formExpired
  };
};

export default useForm;
